import { useState, type ReactNode } from "react";
import { FormattedMessage } from "react-intl";

// Balken-Slider fuer die Zahlenfilter: die Verteilung (model.title/model.
// admissions_de, siehe FilterOptions.distributions) liegt als Balkenreihe
// hinter dem Regler, damit sichtbar wird, wo mengenmaessig die meisten Titel
// liegen - und damit, wo ein Schwellwert die meisten Treffer kostet.
//
// Kein Thumb mehr: die Balken SIND die Eingabeflaeche. Ein Balken unter dem
// Cursor wird signalfarbig, waechst leicht und zeigt ein Tooltip mit dem
// Wert, den ein Klick dort setzen wuerde - ein separater Positions-Marker
// (erster Versuch) war beim Testen nicht zielfuehrend, weil er neben der
// Balkenfarbe ein zweites, schwaecheres Signal war. Der aktive Bereich (vom
// aktuellen Wert bis ans Ende, siehe activeFrom/activeTo) steht dauerhaft in
// VOLLER Signalfarbe, nicht abgeschwaecht - das war vorher verwirrend.
//
// Farbe markiert damit den INTERAKTIONSZUSTAND (Hover, aktueller
// Schwellwert), nicht die Daten selbst - dieselbe Rolle, die bg-accent auch
// bei Toggles/Tabs hat (UI_CONVENTIONS.md). Die Zaehlung pro Bucket bleibt
// allein durch die Balkenhoehe codiert.
//
// Bewusst EIN Schnappschuss ueber den gesamten Katalog, nicht reaktiv auf die
// uebrigen aktiven Filter (siehe numericDistributions im BFF) - eine "echte"
// Facetten-Verteilung waere fuenf Aggregationen ueber 545.680 Zeilen bei jeder
// Filteraenderung.

export interface HistogramBucket {
  value: number;
  count: number;
}

function Bars({
  buckets,
  activeFrom,
  activeTo,
  onBucketClick,
  formatValue,
}: {
  buckets: HistogramBucket[];
  /** Balken ab diesem Wert (inklusive) gelten als "vom Filter erfasst". */
  activeFrom?: number;
  /** Nur beim Bereichs-Regler: obere Grenze, exklusiv. */
  activeTo?: number;
  /** Klick auf einen Balken setzt den Wert direkt auf dessen Bucket-Anfang. */
  onBucketClick: (value: number) => void;
  /** Fuer das Hover-Tooltip: zeigt, welchen Wert ein Klick auf diesen Balken setzen wuerde. */
  formatValue: (value: number) => string;
}) {
  const maxCount = Math.max(1, ...buckets.map((b) => b.count));
  // log statt linear: Stimmen/Besucherzahlen sind schon x-seitig in
  // Zehnerpotenzen gebuckett, aber die ZAEHLUNG pro Bucket bleibt trotzdem
  // extrem rechtsschief (ein Bucket mit 200.000 Titeln neben einem mit 12) -
  // linear presst das kleinere Bucket auf die Mindesthoehe und es wirkt wie
  // "kein Wert". log1p haelt kleine, aber reale Buckets sichtbar.
  const scale = (count: number) => Math.log1p(count) / Math.log1p(maxCount);
  return (
    <div className="absolute inset-x-0 bottom-0 top-1 flex items-end gap-px">
      {buckets.map((b) => {
        const active = activeFrom !== undefined && b.value >= activeFrom && (activeTo === undefined || b.value < activeTo);
        return (
          // group-Wrapper traegt Hoehe + Hover-Bereich; das Tooltip haengt
          // daran, statt an einem festen Marker, und wandert damit automatisch
          // mit der (unterschiedlich hohen) Balkenspitze mit.
          <div key={b.value} className="group relative flex-1" style={{ height: `${Math.max(6, scale(b.count) * 100)}%` }}>
            <div
              // Keine echte <button>: die Tastatur-/Screenreader-Bedienung laeuft
              // ueber den versteckten <input type=range> (siehe HIDDEN_RANGE_INPUT_CLASS),
              // dieser Klick ist eine reine Maus-Komfortflaeche darueber.
              onClick={() => onBucketClick(b.value)}
              className={`h-full min-w-px w-full origin-bottom cursor-pointer rounded-t-[1px] transition-all duration-150 group-hover:z-10 group-hover:scale-125 group-hover:bg-accent ${
                active ? "bg-accent" : "bg-ink/15"
              }`}
            />
            <div
              className="pointer-events-none absolute -top-6 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded bg-ink px-1.5 py-0.5 text-[10px] text-surface opacity-0 transition-opacity group-hover:opacity-100"
              aria-hidden="true"
            >
              {formatValue(b.value)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Labels({ children }: { children: ReactNode }) {
  return <div className="mt-1 flex items-center justify-between text-[11px] text-ink/40 tabular-nums">{children}</div>;
}

// Der native Input bleibt nur fuer Tastatur-Bedienung (Tab + Pfeiltasten) und
// Screenreader im DOM - komplett unsichtbar und pointer-events-none, weil die
// Maus-Interaktion vollstaendig ueber die Balken laeuft (siehe Bars/onBucketClick).
const HIDDEN_RANGE_INPUT_CLASS = "pointer-events-none absolute inset-x-0 bottom-0 h-6 w-full appearance-none bg-transparent opacity-0";

/** Ein Schwellwert-Regler ("ab"-Filter): Klick auf einen Balken setzt den Wert. */
export function TobisMinSlider({
  buckets,
  min,
  max,
  step,
  value,
  onChange,
  formatValue,
  ariaLabel,
}: {
  buckets: HistogramBucket[];
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  formatValue: (value: number) => string;
  ariaLabel: string;
}) {
  return (
    <div className="w-full">
      <div className="relative h-7">
        {/* Bei value===min ist der Filter nicht aktiv (Default) - dann soll
            KEIN Balken als "ausgewaehlt" gelten, auch wenn "value >= min"
            fuer jeden Bucket rechnerisch zutrifft. */}
        <Bars buckets={buckets} activeFrom={value > min ? value : undefined} onBucketClick={onChange} formatValue={formatValue} />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className={HIDDEN_RANGE_INPUT_CLASS}
          aria-label={ariaLabel}
        />
      </div>
      <Labels>
        <span>{formatValue(min)}</span>
        <span className="text-ink/70">{formatValue(value)}</span>
      </Labels>
    </div>
  );
}

/**
 * Bereichs-Regler (z.B. Jahr von/bis), erster Wurf: kein zweiter Thumb mehr,
 * stattdessen zwei Klicks in Folge. Der erste Klick setzt "ab" (verhaelt sich
 * bis dahin wie TobisMinSlider - erfasst alles bis ans Ende), der zweite Klick
 * setzt "bis" und schliesst den Bereich. Zwischen den beiden Klicks steht ein
 * Hinweis unter dem Regler.
 */
export function TobisRangeSlider({
  buckets,
  min,
  max,
  step,
  from,
  to,
  onChange,
  formatValue,
  ariaLabelFrom,
  ariaLabelTo,
}: {
  buckets: HistogramBucket[];
  min: number;
  max: number;
  step: number;
  from: number;
  to: number;
  onChange: (from: number, to: number) => void;
  formatValue: (value: number) => string;
  ariaLabelFrom: string;
  ariaLabelTo: string;
}) {
  const [pendingFrom, setPendingFrom] = useState<number | null>(null);
  // from===min && to===max ist der Default (Filter nicht aktiv) - dann soll
  // wie beim Schwellwert-Regler kein Balken als "ausgewaehlt" gelten.
  const hasSelection = pendingFrom !== null || from > min || to < max;

  const handleBucketClick = (clicked: number) => {
    if (pendingFrom === null) {
      setPendingFrom(clicked);
      onChange(clicked, max);
      return;
    }
    onChange(Math.min(pendingFrom, clicked), Math.max(pendingFrom, clicked));
    setPendingFrom(null);
  };

  return (
    <div className="w-full">
      <div className="relative h-7">
        <Bars
          buckets={buckets}
          activeFrom={pendingFrom ?? (hasSelection ? from : undefined)}
          activeTo={pendingFrom === null && hasSelection ? to : undefined}
          onBucketClick={handleBucketClick}
          formatValue={formatValue}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={from}
          // Nie ueber die obere Grenze hinaus: sonst kippt der Bereich unbemerkt um.
          onChange={(e) => onChange(Math.min(Number(e.target.value), to), to)}
          className={HIDDEN_RANGE_INPUT_CLASS}
          aria-label={ariaLabelFrom}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={to}
          onChange={(e) => onChange(from, Math.max(Number(e.target.value), from))}
          className={HIDDEN_RANGE_INPUT_CLASS}
          aria-label={ariaLabelTo}
        />
      </div>
      <Labels>
        <span>{formatValue(min)}</span>
        <span className="text-ink/70">
          {pendingFrom === null ? `${formatValue(from)} – ${formatValue(to)}` : formatValue(pendingFrom)}
        </span>
        <span>{formatValue(max)}</span>
      </Labels>
      {pendingFrom !== null && (
        <div className="mt-1 text-[11px] text-accent">
          <FormattedMessage id="tobis.filter.rangeSecondClick" defaultMessage="Second click sets the end date" />
        </div>
      )}
    </div>
  );
}
